import { pipeline, env, TextStreamer } from '@huggingface/transformers';

// Always fetch from HuggingFace Hub; cache in browser IndexedDB/Cache API
env.allowLocalModels = false;
env.useBrowserCache = true;

const MODEL_ID = 'HuggingFaceTB/SmolLM2-360M-Instruct';

const SYSTEM_PROMPT = `You are S.O.S Assistant, a helpful AI embedded in Sean's portfolio OS.
Sean is a Lead Software Engineer with expertise in React, Python, Node.js, AWS, Azure, and GCP.
He worked at DXC Technology (2019–2022) leading application migrations and DevOps consulting engagements.
He's an Eagle Scout who loves hiking, camping, and restoring vintage electronics and game consoles.
Keep answers short and helpful. When asked about Sean, answer from the info above.`;

class SmolLMService {
    constructor() {
        this.pipe = null;
        this.device = null;
    }

    /**
     * Load the model. Calls onProgress with progress info during download.
     * Returns the device used: 'webgpu' | 'wasm'
     */
    async init(onProgress) {
            // Detect WebGPU — Safari 18+, Chrome 113+, Edge 113+
        // Falls back to WASM (CPU) on older browsers automatically
        let device = 'wasm';
        if (typeof navigator !== 'undefined' && navigator.gpu) {
            try {
                const adapter = await navigator.gpu.requestAdapter();
                // Some browsers expose navigator.gpu but return null for the adapter
                if (adapter) {
                    // Verify the adapter can actually be used
                    const gpuDevice = await adapter.requestDevice();
                    if (gpuDevice) {
                        device = 'webgpu';
                        gpuDevice.destroy(); // release — pipeline will create its own
                    }
                }
            } catch {
                device = 'wasm';
            }
        }

        this.pipe = await pipeline('text-generation', MODEL_ID, {
            dtype: device === 'webgpu' ? 'q4f16' : 'q4',
            device,
            progress_callback: onProgress,
        });

        this.device = device;
        return device;
    }

    /**
     * Generate a streaming response.
     * @param {Array<{role: string, content: string}>} history
     * @param {function(string): void} onChunk - called with each new text fragment
     * @returns {Promise<string>} full response
     */
    async generate(history, onChunk) {
        if (!this.pipe) throw new Error('Model not loaded');

        const messages = [
            { role: 'system', content: SYSTEM_PROMPT },
            ...history,
        ];

        let full = '';
        const streamer = new TextStreamer(this.pipe.tokenizer, {
            skip_prompt: true,
            skip_special_tokens: true,
            callback_function: (text) => {
                full += text;
                onChunk(text, full);
            },
        });

        await this.pipe(messages, {
            max_new_tokens: 256,
            temperature: 0.7,
            do_sample: true,
            return_full_text: false,
            streamer,
        });

        return full;
    }
}

// Singleton — persists for the lifetime of the browser tab
export const smolLM = new SmolLMService();
